import { ACTIONS, RESOURCES } from '@hvac/core';
import { z } from 'zod';
import { isoDateTimeSchema, localeSchema, uuidSchema } from './common.js';
import { orgRoleSchema } from './membership.js';
import { organizationSchema } from './organization.js';
/**
 * Session — everything the client needs to render a correct, safe UI.
 *
 * Returned by `GET /v1/session`. Notably it includes the resolved permission
 * list rather than just the role, so the frontend never reimplements the
 * permission matrix. The client uses permissions to decide what to *show*; the
 * server and RLS decide what is actually *allowed*.
 */
export const permissionSchema = z.custom((value) => {
    if (typeof value !== 'string')
        return false;
    const [resource, action] = value.split(':');
    return (resource !== undefined &&
        action !== undefined &&
        RESOURCES.includes(resource) &&
        ACTIONS.includes(action));
}, { message: 'must be a "resource:action" permission' });
export const userProfileSchema = z.object({
    id: uuidSchema,
    email: z.email(),
    fullName: z.string().nullable(),
    avatarUrl: z.url().nullable(),
    locale: localeSchema,
    createdAt: isoDateTimeSchema,
});
/** One organisation the signed-in user belongs to, with their role in it. */
export const sessionOrganizationSchema = z.object({
    organization: organizationSchema,
    role: orgRoleSchema,
    permissions: z.array(permissionSchema),
});
export const sessionSchema = z.object({
    user: userProfileSchema,
    organizations: z.array(sessionOrganizationSchema),
    /**
     * The organisation the client should operate in. Null only when the user
     * belongs to none, which the UI must handle explicitly.
     */
    activeOrganizationId: uuidSchema.nullable(),
});
export const updateProfileRequestSchema = z
    .object({
    fullName: z.string().trim().min(1).max(120),
    locale: localeSchema,
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided.',
});
//# sourceMappingURL=session.js.map